import React, { Component } from 'react';
import {
  Alert, Spinner, Table,
} from 'react-bootstrap';

import { PageStatus } from 'enums';
import { Show } from "../../Layout";
import CardHeader from "../Card/CardHeader";
import GridContainer from "../Grid/GridContainer";
import { formatDate } from "../../Utils/formatDate";
import { SurveysAPI } from "../../API";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { PanelistDetails } from "./PanelistDetails";
import { surveyDict, surveyStatusLabels } from 'Languages/SurveyTranslations';
import { withTranslation } from 'react-i18next';

const MODAL_TYPES = {
  NONE: 'NONE',
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  DETAILS: 'DETAILS',
};

type State = {
  status: PageStatus,
  error: string | null,
  formType: string,
  data: any[],
  id?: string | null,
  filteredData: any[],
  filters: {
    dateRange: any,
    name: '',
    client: '',
    publishDate: '',
    expiryDate: '',
    surveyName: '',
    surveyType: ''
  },
};

class PanelistSurveys extends Component<any, any> {
  constructor(props) {
    super(props);
    this.state = {
      status: PageStatus.None,
      error: null,
      formType: MODAL_TYPES.NONE,
      data: [],
      id: null,
      filteredData: [],
      filters: {
        dateRange: '',
        name: '',
        client: '',
        publishDate: '',
        expiryDate: '',
        surveyName: '',
        surveyType: ''
      },
    };
    this.fetchList = this.fetchList.bind(this);
  }

  componentDidMount() {
    this.fetchList()
  }

  fetchList(): Promise<void> {
    return Promise.resolve()
      .then(() => this.setState({ status: PageStatus.Loading }))
      .then(() => SurveysAPI.panelistSurveys(this.props.userId))
      .then((countries) => {
        this.setState({ data: countries, filteredData: countries, status: PageStatus.Loaded });
      })
      .catch((error) => {
        this.setState({ error: error.message, status: PageStatus.Error });
      });
  }

  isShowDetailModal(): boolean {
    return this.state.formType === MODAL_TYPES.DETAILS
      || this.state.formType === MODAL_TYPES.DELETE;
  }


  render() {
    const lang = this.props.language ?? 'en';
    const { t } = this.props;
    // Points = points the panelist actually EARNED for the survey, served by the
    // backend as info.earnedPoints (Rewards ledger, rewardType 'Survey'/Accepted).
    // `?? 0` so a legitimate 0 (not yet earned) shows as 0, never blank.
    const getEarnedPoints = (info) => info?.earnedPoints ?? 0;
    // Derived panelist-facing status, computed by the backend shared helper
    // (utils/surveyStatus.js) and returned as info.derivedStatusKey. Fall back
    // to the raw assignment status if an older payload lacks the derived key.
    const statusLabels = surveyStatusLabels[lang] ?? surveyStatusLabels.en;
    const getDerivedStatusLabel = (info) => {
      const key = info?.derivedStatusKey;
      return (key && statusLabels[key]) ?? info?.status ?? '';
    }
    return (
      <>
        <GridContainer>
          <div className='w-100 mb-3'>
            <CardHeader color="primary">
              <div className="d-flex align-items-center justify-content-between">
                <h4>{t('survey.list.heading')}</h4>
              </div>
            </CardHeader>
          </div>
        </GridContainer>

        <div className="jumbotron bg-white p-3 border shadow-sm">
          <Alert variant="danger" show={this.state.status === PageStatus.Error}>
            {this.state.error}
          </Alert>

          <Show when={this.state.status === PageStatus.Loading}>
            <div className="d-flex justify-content-center w-100 p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          </Show>

          <Show when={this.state.status === PageStatus.Loaded}>
            <Show when={!this.state.filteredData.length}>
              <Alert variant="info" show={!this.state.filteredData.length}>
                {t('survey.list.emptyState')}
              </Alert>
            </Show>

            <Show when={!!this.state.filteredData.length}>
              <Show when={this.isShowDetailModal()}>
                <PanelistDetails
                  language={lang}
                  id={this.state.id}
                  onClose={() => this.setState({
                    formType: MODAL_TYPES.NONE,
                    id: null,
                  })}
                  onUpdate={() => {
                    this.setState({
                      formType: MODAL_TYPES.UPDATE,
                    });
                  }}
                  onDelete={(id) => {
                    this.fetchList();
                    this.setState({
                      formType: MODAL_TYPES.NONE,
                      id: null,
                    });
                  }}
                />
              </Show>

              <Table responsive size="sm" bordered>
                <thead>
                  <tr>
                    <th>{surveyDict[lang]["S.No"] || "S.No"}</th>
                    <th>{surveyDict[lang]["Name"] || "Name"}</th>
                    <th>{surveyDict[lang]["Status"] || "Status"}</th>
                    <th>{surveyDict[lang]["Points"] || "Points"}</th>
                    <th>{surveyDict[lang]["End Date"] || "End Date"}</th>
                    <th>{surveyDict[lang]["Action"] || "Start Survey"}</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.filteredData.map((info, index) => (
                      <tr key={"filteredData" + index}>
                        <td>{index + 1}</td>
                        <td>
                          <button
                            className="text-primary bg-transparent"
                            onKeyPress={() => null}
                            onClick={() => {
                              this.setState({
                                formType: MODAL_TYPES.DETAILS,
                                id: info.id,
                              });
                            }}
                            dangerouslySetInnerHTML={{
                              __html: info?.survey?.name ?? (surveyDict[lang]["Surveys"] || 'Title'),
                            }}
                          ></button>
                        </td>
                        <td>{getDerivedStatusLabel(info)}</td>
                        <td>{getEarnedPoints(info)}</td>
                        <td>{formatDate(info.expiryDate)}</td>
                        <td>
                          {info?.isTakeable && info?.temporarySurveyLink ? (
                            <a href={info.temporarySurveyLink} target="_blank" rel="noopener noreferrer">
                              {surveyDict[lang]["Click Here to start survey"] || "Click here to start survey"}
                            </a>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </Table>
            </Show>
          </Show>
        </div>
      </>

    );
  }
}


const mapStateToProps = (state: { adminUser: { adminUser: { phoneNumber: any, email: any, userId: any; token: any; loading: any; error: any; role: any, language: any }; }; }) => {
  return {
    userId: state.adminUser.adminUser.userId,
    language: state.adminUser.adminUser.language
  };
};

const PanelistSurveysWithRouter = withRouter(connect(mapStateToProps)(withTranslation()(PanelistSurveys)));

export { PanelistSurveysWithRouter as PanelistSurveys };
